import { connect } from 'react-redux';
import { createRecordingDialogEvent } from '../../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../../analytics/functions';
import { translate } from '../../../../base/i18n/functions';
import { JitsiRecordingConstants } from '../../../../base/lib-jitsi-meet';
import { setRequestingSubtitles } from '../../../../subtitles/actions.any';
import { toggleScreenshotCaptureSummary } from '../../../../screenshot-capture/actions';
import { RECORDING_METADATA_ID } from '../../../constants';
import { getActiveSession } from '../../../functions';
import AbstractRecordButton, { _mapStateToProps as _abstractMapStateToProps } from '../AbstractRecordButton';
/**
 * Button for opening a dialog where a recording session can be started.
 */
class RecordingButton extends AbstractRecordButton {
    /**
     * True between a start/stop click and the recording state actually flipping.
     * `_isRecordingRunning` (canStopRecording) lags the click by a few seconds
     * while the session ON/OFF status propagates — without this guard the button
     * looks unchanged ("frozen") and a second click fires a duplicate stop that
     * jicofo rejects as an unknown session.
     */
    _transitioning = false;
    _transitionTimeout;
    /**
     * Clears the transition guard when the recording state has actually changed
     * (or via the safety timeout).
     *
     * @param {IProps} prevProps - Previous props.
     * @returns {void}
     */
    componentDidUpdate(prevProps) {
        if (this._transitioning && prevProps._isRecordingRunning !== this.props._isRecordingRunning) {
            this._transitioning = false;
            clearTimeout(this._transitionTimeout);
            this.forceUpdate();
        }
    }
    /**
     * Cleanup.
     *
     * @returns {void}
     */
    componentWillUnmount() {
        clearTimeout(this._transitionTimeout);
    }
    /**
     * Disabled while a start/stop is in flight, so the button gives immediate
     * feedback and can't be double-clicked.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isDisabled() {
        return this.props._disabled || this._transitioning;
    }
    /**
     * Handles clicking / pressing the button.
     *
     * @override
     * @protected
     * @returns {void}
     */
    _onHandleClick() {
        if (this._transitioning) {
            return;
        }
        const { _isRecordingRunning } = this.props;
        // Guard the click and re-render so the button greys out immediately.
        this._transitioning = true;
        clearTimeout(this._transitionTimeout);
        // Safety: clear the guard even if the state update never arrives.
        this._transitionTimeout = window.setTimeout(() => {
            this._transitioning = false;
            this.forceUpdate();
        }, 8000);
        this.forceUpdate();
        if (_isRecordingRunning) {
            // Stop recording directly without showing dialog
            this._stopRecording();
        }
        else {
            // Start recording directly without dialog
            this._startRecording();
        }
    }
    /**
     * Starts recording directly with default settings using Jibri.
     *
     * @private
     * @returns {void}
     */
    _startRecording() {
        const { _conference, dispatch } = this.props;
        if (_conference) {
            // Enable tile view before starting recording to ensure correct layout
            import('../../../../video-layout/actions.web').then(({ setTileView }) => {
                dispatch(setTileView(true));
            });
            // Start Jibri recording with file recording metadata
            const appData = JSON.stringify({
                'file_recording_metadata': {
                    'share': false // You can set this to true if you want sharing enabled by default
                }
            });
            _conference.startRecording({
                mode: JitsiRecordingConstants.mode.FILE,
                appData
            });
        }
    }
    /**
     * Stops the recording if it's currently running.
     *
     * @private
     * @returns {void}
     */
    _stopRecording() {
        const { _conference, _displaySubtitles, _fileRecordingSession, _subtitlesLanguage, dispatch } = this.props;
        sendAnalytics(createRecordingDialogEvent('stop', 'confirm.button'));
        // Only handle Jibri (file) recordings
        if (_fileRecordingSession) {
            _conference?.stopRecording(_fileRecordingSession.id);
            dispatch(toggleScreenshotCaptureSummary(false));
        }
        dispatch(setRequestingSubtitles(Boolean(_displaySubtitles), _displaySubtitles, _subtitlesLanguage));
        _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
            isTranscribingEnabled: false
        });
    }
}
/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code RecordButton} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _conference: IJitsiConference,
 *     _fileRecordingsDisabledTooltipKey: ?string,
 *     _isRecordingRunning: boolean,
 *     _disabled: boolean,
 *     visible: boolean
 * }}
 */
export function _mapStateToProps(state) {
    const abstractProps = _abstractMapStateToProps(state);
    const { toolbarButtons } = state['features/toolbox'];
    const visible = Boolean(toolbarButtons?.includes('recording') && abstractProps.visible);
    const { _displaySubtitles, _language: _subtitlesLanguage } = state['features/subtitles'];
    return {
        ...abstractProps,
        _conference: state['features/base/conference'].conference,
        _displaySubtitles,
        _fileRecordingSession: getActiveSession(state, JitsiRecordingConstants.mode.FILE),
        _subtitlesLanguage,
        visible
    };
}
export default translate(connect(_mapStateToProps)(RecordingButton));
